import { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { LEGAL_DOCS } from '../../legal';
import { toast } from 'sonner';

/**
 * LegalAcceptanceGate
 * -------------------
 * Mounts once inside the authed shell. On mount + whenever `user` changes:
 *   1. calls GET /api/legal/acceptance-status
 *   2. if `needs_acceptance` is non-empty, renders a modal that walks the
 *      user through each doc, calling POST /api/legal/accept on click.
 *   3. does nothing if the user is not logged in (localStorage.user empty).
 *
 * Kept intentionally opinion-light so it can wrap any of the four apps
 * (ConsumerApp, RetailerApp, AdminApp, CharityApp) without extra props.
 */
export default function LegalAcceptanceGate() {
  const [pending, setPending] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPending([]);
      return;
    }
    try {
      const res = await api.get('/legal/acceptance-status');
      setPending(res.data.needs_acceptance || []);
      setCurrentIdx(0);
      setScrolledToBottom(false);
    } catch (e) {
      // 401 (unauth) or 5xx — silently swallow; user can log in normally.
      setPending([]);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Cross-tab login/logout (storage event only fires in *other* tabs)
    const onStorage = (e) => { if (e.key === 'token') refresh(); };
    // Same-tab login/logout — pages dispatch this event after writing to
    // localStorage so the gate re-checks without a page reload.
    const onAuthChanged = () => refresh();
    window.addEventListener('storage', onStorage);
    window.addEventListener('auth-changed', onAuthChanged);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth-changed', onAuthChanged);
    };
  }, [refresh]);

  if (pending.length === 0) return null;

  const cur = pending[currentIdx];
  const doc = LEGAL_DOCS[cur.doc_id];
  if (!doc) return null;

  const handleAccept = async () => {
    setBusy(true);
    try {
      await api.post('/legal/accept', {
        doc_id: cur.doc_id,
        version: cur.current_version,
      });
      const next = currentIdx + 1;
      if (next >= pending.length) {
        toast.success('Thanks — all documents accepted.');
        setPending([]);
      } else {
        setCurrentIdx(next);
        setScrolledToBottom(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not record acceptance.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      setScrolledToBottom(true);
    }
  };

  const bodyText = typeof doc.body === 'string' ? doc.body : (doc.body?.en || '');

  return (
    <div
      data-testid="legal-acceptance-gate"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9000, backdropFilter: 'blur(4px)',
      }}
    >
      <div
        role="dialog" aria-modal="true"
        style={{
          background: '#fff', borderRadius: 12, maxWidth: 720, width: '92%',
          maxHeight: '86vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(15,23,42,0.35)',
        }}
      >
        <header style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
              {doc.title}
            </h2>
            <span style={{ color: '#64748b', fontSize: 13 }}>
              Document {currentIdx + 1} of {pending.length}
            </span>
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
            Effective {doc.effectiveDate || 'today'} · Version {doc.version || cur.current_version || 1}
          </p>
        </header>

        <div
          onScroll={handleScroll}
          data-testid="legal-acceptance-body"
          style={{
            padding: '20px 24px', overflowY: 'auto', flex: 1,
            fontSize: 14, lineHeight: 1.6, color: '#334155',
            whiteSpace: 'pre-wrap',
          }}
        >
          {bodyText}
        </div>

        <footer
          style={{
            padding: '16px 24px', borderTop: '1px solid #e2e8f0',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          }}
        >
          <span style={{ fontSize: 12, color: scrolledToBottom ? '#059669' : '#94a3b8' }}>
            {scrolledToBottom ? '✓ You have read to the end.' : 'Scroll to the end to enable Accept.'}
          </span>
          <button
            data-testid="legal-acceptance-accept-btn"
            disabled={!scrolledToBottom || busy}
            onClick={handleAccept}
            style={{
              background: !scrolledToBottom || busy ? '#94a3b8' : '#2563eb',
              color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 22px', fontSize: 14, fontWeight: 600,
              cursor: !scrolledToBottom || busy ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Recording…' : `Accept "${doc.title}"`}
          </button>
        </footer>
      </div>
    </div>
  );
}
